import type { UserRole } from '@familychef/shared';
import { useState } from 'react';

import { useUsers, type UserRow } from '../../hooks/useUsers';

import { DataTable } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { SearchBar } from '@/components/common/SearchBar';
import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { useNavigate } from '@/lib/router';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const ROLE_BADGE: Record<UserRole, { label: string; className: string }> = {
  user: { label: 'User', className: 'bg-slate-100 text-slate-600' },
  moderator: { label: 'Mod', className: 'bg-blue-100 text-blue-700' },
  admin: { label: 'Admin', className: 'bg-orange-100 text-orange-700' },
};

export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data = [], isLoading } = useUsers(search);

  const columns = [
    {
      key: 'email',
      header: 'Email',
      cell: (row: UserRow) => <span className="font-medium text-slate-900">{row.email}</span>,
    },
    {
      key: 'name',
      header: 'Nom',
      className: 'hidden sm:table-cell',
      cell: (row: UserRow) => <span className="text-slate-600">{row.name ?? '—'}</span>,
    },
    {
      key: 'role',
      header: 'Rôle',
      className: 'hidden md:table-cell',
      cell: (row: UserRow) => {
        const badge = ROLE_BADGE[row.role];
        return (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
          >
            {badge.label}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      header: 'Inscription',
      className: 'hidden lg:table-cell',
      cell: (row: UserRow) => <span className="text-slate-500">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: (row: UserRow) => (
        <button
          onClick={() => void navigate({ to: `/users/${row.id}` })}
          className="text-sm text-green-600 hover:text-green-700"
        >
          Voir →
        </button>
      ),
    },
  ];

  return (
    <AppShell>
      <TopBar
        title="Utilisateurs"
        subtitle={`${data.length} utilisateur${data.length > 1 ? 's' : ''}`}
      />
      <div className="p-4 md:p-6">
        <PageHeader title="Gestion des utilisateurs" />
        <div className="mb-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par email ou nom..."
          />
        </div>
        <DataTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          getRowKey={(r) => r.id}
          emptyMessage="Aucun utilisateur trouvé"
        />
      </div>
    </AppShell>
  );
}
